import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Clock,
  ChevronRight,
  Tag,
  Share2,
  CheckCircle2,
  HelpCircle,
  MessageSquare,
  BookOpen,
  Check,
  Menu,
  X,
  ArrowRight
} from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { BLOG_POSTS } from './BlogData';

const BlogPostPage = () => {
  const { slug }            = useParams();
  const [copied, setCopied] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  const post    = BLOG_POSTS.find(p => p.slug === slug);
  const related = BLOG_POSTS.filter(p => p.slug !== slug).slice(0, 2);

  if (!post) {
    return (
      <div className="min-h-screen bg-white text-gray-900 flex flex-col items-center justify-center p-6">
        <h1 className="text-2xl font-bold mb-3 text-gray-900">Artículo no encontrado</h1>
        <p className="text-gray-500 text-sm mb-6">El artículo que buscás no existe o fue movido.</p>
        <Link to="/blog" className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-white font-medium text-sm transition-colors">
          Volver al Blog
        </Link>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({ title: post.title, url: window.location.href }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 antialiased">
      <Helmet>
        <title>{`${post.title} | NeuraSur`}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.keywords} />
        <link rel="canonical" href={`https://www.neurasur.com/blog/${post.slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://www.neurasur.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.metaDescription} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BlogPosting",
          "headline": post.title, "description": post.metaDescription,
          "author": { "@type": "Organization", "name": post.author.name, "url": "https://www.neurasur.com" },
          "publisher": { "@type": "Organization", "name": "NeuraSur" },
          "datePublished": post.publishedDate, "dateModified": post.updatedDate
        })}</script>
      </Helmet>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2.5">
            <div className="bg-gray-100 p-1.5 rounded-md">
              <img src="/logo.webp" onError={e => { e.target.src = '/logo.png'; }} alt="NeuraSur" className="h-8 w-auto" />
            </div>
            <span className="font-semibold text-gray-900 text-base">NeuraSur</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link to="/servicios/power-bi-business-intelligence" className="px-3.5 py-2 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors">
              Servicios
            </Link>
            <Link to="/blog" className="px-3.5 py-2 rounded-lg text-sm font-semibold text-emerald-700 bg-emerald-50 rounded-lg transition-colors">
              Blog
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={`https://wa.me/5491171299730?text=${encodeURIComponent(`Hola! Leí "${post.title}" y quiero hacer una consulta.`)}`}
              target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Consultar <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button onClick={() => setMenuOpen(!menuOpen)} className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-700">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden overflow-hidden border-t border-gray-200 bg-white">
              <div className="px-5 py-4 space-y-1 bg-white">
                <Link to="/servicios/power-bi-business-intelligence" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Servicios
                </Link>
                <Link to="/blog" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
                  Blog
                </Link>
                <a
                  href={`https://wa.me/5491171299730?text=${encodeURIComponent(`Hola! Leí "${post.title}" y quiero hacer una consulta.`)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="block text-center mt-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium"
                >
                  Consultar
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ── Article ── */}
      <main className="pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-5 sm:px-8">

          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-gray-400 mb-8 flex-wrap">
            <Link to="/" className="hover:text-gray-600 transition-colors">Inicio</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/blog" className="hover:text-gray-600 transition-colors">Blog</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-gray-600 line-clamp-1">{post.title}</span>
          </div>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mb-5">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">
                <Tag className="w-3 h-3" /> {post.category}
              </span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
              <span className="text-gray-400">· {post.updatedDate}</span>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
              {post.title}
            </h1>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              {post.subtitle}
            </p>

            {/* Author + Share */}
            <div className="mt-6 pt-5 border-t border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700">NS</div>
                <div>
                  <p className="text-sm font-medium text-gray-900 leading-none">{post.author.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{post.author.role}</p>
                </div>
              </div>
              <button
                onClick={handleShare}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-200 text-xs text-gray-500 hover:text-gray-900 hover:border-gray-300 transition-colors"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Share2 className="w-3.5 h-3.5" />}
                {copied ? '¡Copiado!' : 'Compartir'}
              </button>
            </div>
          </header>

          {/* Table of Contents */}
          {post.tableOfContents?.length > 0 && (
            <div className="mb-10 bg-gray-50 border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                <BookOpen className="w-3.5 h-3.5" /> En este artículo
              </p>
              <ul className="space-y-1.5">
                {post.tableOfContents.map(toc => (
                  <li key={toc.id}>
                    <a href={`#${toc.id}`} className="text-sm text-gray-600 hover:text-emerald-600 transition-colors flex items-center gap-2">
                      <ChevronRight className="w-3.5 h-3.5 text-gray-300 shrink-0" />
                      {toc.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Body */}
          <div className="space-y-5 text-[15px] text-gray-600 leading-relaxed">
            {post.content.map((block, i) => {
              if (block.type === 'heading') return (
                <h2 key={i} id={block.id} className="text-xl sm:text-2xl font-bold text-gray-900 mt-10 mb-1">
                  {block.text}
                </h2>
              );
              if (block.type === 'paragraph') return (
                <p key={i}>{block.text}</p>
              );
              if (block.type === 'list') return (
                <ul key={i} className="space-y-2.5 my-4">
                  {block.items.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2.5 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              );
              if (block.type === 'callout') return (
                <div key={i} className="my-6 border-l-2 border-emerald-400 bg-emerald-50 rounded-r-xl pl-5 pr-5 py-4">
                  <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-1">{block.title}</p>
                  <p className="text-sm text-gray-700 leading-relaxed">{block.text}</p>
                </div>
              );
              return null;
            })}
          </div>

          {/* FAQs */}
          {post.faqs?.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-5 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-emerald-500" /> Preguntas frecuentes
              </h3>
              <div className="space-y-3">
                {post.faqs.map((faq, i) => (
                  <div key={i} className="bg-gray-50 border border-gray-200 rounded-xl p-5">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">{faq.question}</h4>
                    <p className="text-sm text-gray-500 leading-relaxed">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Inline CTA */}
          <div className="mt-12 bg-gray-900 rounded-2xl px-7 py-8 text-center">
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">¿Querés aplicarlo en tu empresa?</h3>
            <p className="text-sm text-gray-400 mb-5 max-w-md mx-auto">
              Coordinamos un diagnóstico gratuito de 30 minutos para identificar por dónde empezar.
            </p>
            <a
              href={`https://wa.me/5491171299730?text=${encodeURIComponent(`Hola! Leí "${post.title}" y quiero coordinar una llamada.`)}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-gray-950 font-semibold text-sm transition-colors"
            >
              <MessageSquare className="w-4 h-4" /> Hablar con un especialista
            </a>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-12 pt-8 border-t border-gray-200">
              <h3 className="text-base font-bold text-gray-900 mb-5">Artículos relacionados</h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {related.map(rel => (
                  <Link key={rel.slug} to={`/blog/${rel.slug}`}
                    className="group bg-gray-50 border border-gray-200 hover:border-gray-300 rounded-xl p-5 transition-colors"
                  >
                    <span className="text-[11px] text-emerald-600 font-medium">{rel.category}</span>
                    <h4 className="text-sm font-semibold text-gray-900 group-hover:text-emerald-700 mt-1 transition-colors leading-snug">{rel.title}</h4>
                    <p className="text-xs text-gray-400 mt-1.5 line-clamp-2">{rel.subtitle}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ── Footer ── */}
      <footer className="border-t border-gray-200 py-8 px-5 sm:px-8 bg-gray-50/50">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-600 font-medium">
          <p>© 2026 NeuraSur. Todos los derechos reservados.</p>
          <p className="text-gray-500">
            Diseño e implementación por{' '}
            <a 
              href="https://www.neurasur.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors underline-offset-4 hover:underline"
            >
              NeuraSur
            </a>
          </p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-emerald-600 transition-colors">Inicio</Link>
            <Link to="/blog" className="hover:text-emerald-600 transition-colors">Blog</Link>
            <Link to="/servicios/power-bi-business-intelligence" className="hover:text-emerald-600 transition-colors">Power BI</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
